import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit, ChevronRight } from 'lucide-react';
import { apiFetch } from '../utils/api';
import { useTranslation } from 'react-i18next';

const CoachWorkoutDetailPage = () => {
  const pathParts = window.location.pathname.split('/');
  const athleteId = pathParts[3];
  const workoutId = pathParts[5];

  const navigate = useNavigate();
  const { t } = useTranslation();
  const [workout, setWorkout] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [activeDay, setActiveDay] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([fetchWorkout(), fetchSessions()]).finally(() => setLoading(false));
  }, [workoutId]);

  const fetchWorkout = async () => {
    try {
      const res = await apiFetch(`/api/coach/athlete/${athleteId}/workouts/${workoutId}`);
      if (res.ok) setWorkout(await res.json());
    } catch (err) {
      console.error(err);
    }
  };

  const fetchSessions = async () => {
    try {
      const res = await apiFetch(`/api/coach/athlete/${athleteId}/sessions?workout_id=${workoutId}`);
      if (res.ok) setSessions(await res.json());
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) return <div className="loading"><div className="loading-spinner" /></div>;

  if (!workout) return (
    <div className="page-container">
      <div className="empty-state">
        <h3 className="empty-title">{t('workout_detail.not_found')}</h3>
        <button className="btn btn-primary" onClick={() => navigate(`/coach/athlete/${athleteId}`)}>
          {t('common.back')}
        </button>
      </div>
    </div>
  );

  const currentDay = workout.days?.[activeDay];

  return (
    <div className="page-container animate-fade-in">
      <div className="header">
        <div>
          <button
            className="btn btn-sm btn-secondary mb-2"
            onClick={() => navigate(`/coach/athlete/${athleteId}`)}
          >
            {t('common.back')}
          </button>
          <h1 className="header-title">{workout.name}</h1>
          {workout.description && (
            <p className="text-muted" style={{ fontSize: '13px', marginTop: '2px' }}>
              {workout.description}
            </p>
          )}
        </div>
        <button
          className="btn btn-sm btn-secondary"
          style={{ gap: '6px', flexShrink: 0, alignSelf: 'flex-start', marginTop: '32px' }}
          onClick={() => navigate(`/coach/athlete/${athleteId}/workout/${workoutId}/edit`)}
        >
          <Edit size={14} />
          {t('common.update')}
        </button>
      </div>

      <div className="day-tabs">
        {workout.days?.map((day, index) => (
          <button
            key={index}
            className={`day-tab ${activeDay === index ? 'active' : ''}`}
            onClick={() => setActiveDay(index)}
          >
            {day.name}
          </button>
        ))}
      </div>

      {currentDay?.exercises?.length > 0 ? (
        currentDay.exercises.map((exercise, index) => (
          <div key={index} className="exercise-item">
            <div className="exercise-header">
              <span className="exercise-name">{exercise.name}</span>
              <span className="exercise-sets">{exercise.sets}</span>
            </div>
            {exercise.notes && (
              <p className="exercise-notes">{exercise.notes}</p>
            )}
          </div>
        ))
      ) : (
        <div className="empty-state" style={{ padding: '32px 0' }}>
          <p className="text-muted">{t('workout_detail.no_exercises')}</p>
        </div>
      )}

      <div className="mt-6">
        <h3 style={{ fontSize: '16px', marginBottom: '12px', color: 'var(--foreground)' }}>
          {t('coach_workout.sessions_title')}
        </h3>

        {sessions.length === 0 ? (
          <div className="empty-state" style={{ padding: '24px 0' }}>
            <p className="text-muted">
              {t('coach_workout.no_sessions')}
            </p>
          </div>
        ) : (
          sessions.slice(0, 10).map((session) => (
            <div
              key={session.session_id}
              className="card mb-2"
              style={{ padding: '12px' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <p style={{ fontWeight: '600', color: 'var(--foreground)', marginBottom: '2px' }}>
                    {session.day_name}
                  </p>
                  <p className="text-muted" style={{ fontSize: '12px' }}>{session.date}</p>
                </div>
                <span style={{ color: 'var(--primary)', fontSize: '12px' }}>
                  {t('workout_detail.exercises_count', { count: session.exercises?.length || 0 })}
                </span>
              </div>

              {session.exercises?.length > 0 && (
                <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {session.exercises.map((ex, i) => (
                    <div key={i} style={{
                      display: 'flex', justifyContent: 'space-between',
                      fontSize: '12px', color: 'var(--muted-foreground)',
                      padding: '4px 8px',
                      borderRadius: '6px',
                      background: 'rgba(255,255,255,0.04)'
                    }}>
                      <span style={{ flex: 1 }}>{ex.exercise_name}</span>
                      <span style={{ color: 'var(--foreground)', fontWeight: '600' }}>
                        {ex.weight && `${ex.weight}kg`}
                        {ex.weight && ex.reps && ' × '}
                        {ex.reps && `${ex.reps}`}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CoachWorkoutDetailPage;
